import React, { useCallback, useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { Tooltip } from "@mui/material";
import { ArrowSquareOutIcon, PlusIcon } from "@phosphor-icons/react";
import {
  FactDefinition,
  ScalarFact,
  ArrayFact,
  FactValue,
  ScalarKind,
  ShallowEntity,
  factDisplayName,
} from "../types";
import PendingReviewBadge from "./PendingReviewBadge";
import EntitySearchInput from "./EntitySearchInput";
import FactValueChips from "./FactValueChips";
import InputTypeRenderer from "./InputTypeRenderer";
import DateInput from "../Inputs/DateInput";
import ScalarInput from "../Inputs/ScalarInput";
import HierarchicalArrayInput from "../Inputs/HierarchicalArrayInput";
import StatusDropdown from "./StatusDropdown";
import type { ToneSource } from "../../design/enumTone";
import { useAnswerStore } from "../../../../FpFundBrain/Matrix/AnswerDialog";
import {
  InlineFieldAutofillControl,
  useAutofillStatusStore,
} from "./AutofillTriggers";
import { useEntityDraftStore } from "../Tabs/FactsTab";
import { designatedCreateKind } from "../../Setup/designations";
import { useCanEditRms } from "@/hooks/useCanEditRms";
import { useTypewriter } from "@/hooks/productTimerHooks";
import { factSourceFor } from "../../factSource";
import { openChatCitation } from "@/components/common/openChatCitation";

// Define proper types for fact values
type ScalarFactValue = FactValue;
type ArrayFactValue = FactValue[];

// Positioning for the "[✨ Autofill]" / "[↗ Source]" chips painted as
// adornments inside a fact's input; pair with `left-2` or `right-2`.
//
// `flex` is load-bearing. Both chips are inline-flex buttons, so in a plain
// block wrapper they land in a line box whose strut comes from the page's
// inherited typography — and when that strut's ascent exceeds the button's,
// the chip is pushed below the wrapper's top edge by an amount that tracks
// the ancestor line-height (#10755). A flex wrapper generates no line box,
// so the chip sits exactly where `top` puts it.
//
// 9px is the input's border (1px) plus its vertical padding (`py-2`), i.e.
// where the input's own first line of text starts. Both that line and the
// chip are 20px tall, so the chip aligns with the text — which in a standard
// single-line fact input (38px) is dead center, and in a textarea keeps the
// chip on the first line rather than floating to the middle of the box.
const CHIP_ADORNMENT_CLASSES = "absolute top-[9px] z-10 flex";

// Stable JSON serializer that sorts object keys at every level. Used for
// dirty-state comparisons so values like the structured `address` JSON
// (which round-trip through PostgreSQL JSONB and come back with normalized
// key order) don't get flagged as dirty after save.
const stableStringify = (value: unknown): string => {
  if (value === null || typeof value !== "object") return JSON.stringify(value);
  if (Array.isArray(value)) {
    return "[" + value.map(stableStringify).join(",") + "]";
  }
  const keys = Object.keys(value as Record<string, unknown>).sort();
  return (
    "{" +
    keys
      .map(
        (k) =>
          JSON.stringify(k) +
          ":" +
          stableStringify((value as Record<string, unknown>)[k])
      )
      .join(",") +
    "}"
  );
};

interface EditableFactRendererProps {
  definition: FactDefinition;
  fact: ScalarFact | ArrayFact;
  onChange?: (
    isDirty: boolean,
    draftValue: ScalarFactValue | ArrayFactValue
  ) => void;
  compact?: boolean; // Compact vertical layout for table cell popover
  // A draft the editor should open on instead of the stored value (#11880).
  // A Facts row saves when it closes and reads the value it is saving in
  // the meantime; if the user reopens it, or the save is rejected, the
  // editor has to come back holding what they typed, not what the server
  // still holds. Dirtiness is still measured against `fact`, so a draft
  // equal to the stored value simply opens clean.
  draft?: ScalarFactValue | ArrayFactValue;
  currentEntityId?: string | null; // Current entity ID to exclude from search results (prevents self-reference)
  portalDropdown?: boolean; // Portal entity search dropdowns to document.body (needed when rendered inside a clipping container)
  // Optional inline action slot rendered next to the field label. Used by
  // the Facts tab to attach a per-field "Autofill with AI" sparkle button
  // when the attribute has ai_autofill=true. Kept as a render prop so
  // EditableFactRenderer stays decoupled from autofill specifics.
  renderInlineAction?: () => React.ReactNode;
  // Per-field AI autofill context (issue #4914). When provided, the
  // renderer shows a leading "[✨ Autofill]" chip inside the input
  // whenever the local draft is empty (either because it was never set
  // or the user cleared it) and a trailing "[↗ Source]" chip when the
  // current value came from an AI autofill run.
  autofillContext?: {
    publicEntityId: string;
    attributeId: number;
    // The fact's `autofill_last_outcome_at`: what a dispatch from the chip
    // measures the record against until the run's own row lands (#12035).
    lastOutcomeAt: string | null;
  };
  // The create dialog hides the schema-kind sub-label ("Aa Text", "$
  // Dollar", "(List)") — internal metadata on a create form (#11426
  // component sweep, M1). Edit surfaces keep it.
  showTypeMeta?: boolean;
  // The Facts tab's row grid aligns the open editor to its read rows —
  // the same 148px key column, no card padding — so the row doesn't jump
  // when the editor opens (#11426 nooks N6). The create dialog and card
  // surfaces keep the wider third-column layout.
  layout?: "card" | "grid";
}

const EditableFactRenderer: React.FC<EditableFactRendererProps> = ({
  definition,
  fact,
  onChange,
  layout = "card",
  compact = false,
  draft,
  currentEntityId = null,
  portalDropdown = false,
  renderInlineAction,
  autofillContext,
  showTypeMeta = true,
}) => {
  const userData = useSelector((state: any) => state.userData);
  const arrayDraft = Array.isArray(draft) ? draft : undefined;
  const scalarDraft =
    draft !== undefined && !Array.isArray(draft) ? draft : undefined;
  const canEditRms = useCanEditRms();
  const isArray = "array" in fact;
  // While an autofill run is in-flight for this attribute, lock the
  // editor so the user can't race their own typed input against the
  // incoming AI write (which would land moments later and clobber it).
  const autofillStatus = useAutofillStatusStore((s) =>
    autofillContext ? s.statuses[autofillContext.attributeId] : undefined
  );
  const clearAutofillStatus = useAutofillStatusStore((s) => s.clearStatus);
  const isAutofillPending = autofillStatus === "pending";
  // 409 conflict surfaced by the entity PATCH handler. When set for
  // this field, paint it red and show the live DB value so the user
  // knows what they're about to overwrite. Subscribed scalarly so the
  // renderer only re-renders when this field's conflict state flips.
  const conflict = useEntityDraftStore((s) => s.conflictFacts[definition.name]);
  // A fact row shows a "pending review" icon when the scalar value is
  // staged, or (for arrays) when any element is staged. We intentionally
  // roll up to one icon per attribute row rather than per-position: the
  // entity-level Approve All button is the only action, so sub-position
  // granularity would add noise without utility.
  const hasStagedValue = isArray
    ? (fact as ArrayFact).array.some((v) => v.staging)
    : Boolean((fact as ScalarFact).staging);

  // Source-link chip for autofilled facts. For scalars we read queryId
  // off the fact itself; for array facts (like an entity-typed "Clients"
  // list) each row carries its own queryId — all array members from one
  // autofill run share the same query, so we surface whichever one we
  // can find as the chip's target.
  const scalarFact = !isArray ? (fact as ScalarFact) : null;
  const arrayFirstWithQuery = isArray
    ? ((fact as ArrayFact).array.find((v) => v.queryId && v.workspaceId) ??
      null)
    : null;
  const sourceQueryId =
    scalarFact?.queryId ?? arrayFirstWithQuery?.queryId ?? null;
  const sourceWorkspaceId =
    scalarFact?.workspaceId ?? arrayFirstWithQuery?.workspaceId ?? null;

  // Chat-write provenance (#11499): the RMS agent tool stamps the
  // conversation/turn that authorized the write on every fact row it
  // inserts. Same Source chip slot as autofill; the target is the chat
  // conversation, anchored to the authorizing turn. A named-agent write has
  // a chip only when it cited a document, which the chip opens. When the
  // viewing user can't open the source, the chip stays but degrades to
  // an attributed tooltip instead of a dead link.
  // Shared with the grid's right-click Source entry, so both surfaces agree
  // on where the chip points and what it says when it can't point anywhere
  // (#11677).
  const chatSource = factSourceFor(
    isArray ? (fact as ArrayFact).array : scalarFact
  );
  const sourceHref = chatSource?.href ?? null;
  const sourceViewable = chatSource?.viewable ?? false;
  const sourceTooltip = chatSource?.tooltip ?? "";
  // A value the agent took from a document opens that document on the cited
  // page, as a chat citation does (#14584), instead of the conversation.
  const sourceDocument = chatSource?.document ?? null;

  // Track local draft emptiness so the Autofill chip can re-appear the
  // moment the user clears a previously-filled AI field — not only
  // after the save round-trip. We seed from the server value and update
  // via a handleChange intercept so nothing depends on a parent re-pass.
  //
  // Inputs like <input>/<textarea> emit `""` when the user clears the
  // field rather than `null`, so an equality check against null alone
  // misses the most common "empty" case (the one this whole signal
  // exists to catch). We treat empty / whitespace-only strings as
  // empty too.
  const isEmptyScalarLike = (raw: unknown): boolean => {
    if (raw === null || raw === undefined) return true;
    if (typeof raw === "string" && raw.trim() === "") return true;
    // Structured scalars (address: { formatted, validated, ... }) store
    // "empty" as an object with a blank formatted field rather than null,
    // so the plain null/string check would miss them.
    if (
      typeof raw === "object" &&
      raw !== null &&
      "formatted" in raw &&
      typeof (raw as { formatted: unknown }).formatted === "string" &&
      (raw as { formatted: string }).formatted.trim() === ""
    ) {
      return true;
    }
    return false;
  };
  const computeIsEmpty = (
    value: ScalarFactValue | ArrayFactValue | undefined
  ): boolean => {
    if (value === undefined) {
      return isArray
        ? (fact as ArrayFact).array.length === 0
        : isEmptyScalarLike((fact as ScalarFact).scalar) &&
            fact.entity === null;
    }
    if (Array.isArray(value)) return value.length === 0;
    return isEmptyScalarLike(value.scalar) && value.entity === null;
  };
  const [isLocallyEmpty, setIsLocallyEmpty] = useState(
    computeIsEmpty(undefined)
  );
  // Also track dirty-vs-saved so we can hide the Source chip once the
  // user has typed over the AI-generated value: the stored queryId
  // points at the original answer, not at whatever they just wrote.
  const [isDirty, setIsDirty] = useState(false);
  // True while the input in the right column owns focus. Used to hide
  // the Autofill chip while the user is actively searching/typing — the
  // chip sits in the leading adornment area and would otherwise obscure
  // the search text. Maintained via document-level focusin/focusout
  // listeners (rather than React onFocus/onBlur) because React skips
  // synthetic blur when the focused child is unmounted — which is the
  // common path here (remove last chip → chip's X button unmounts →
  // onBlur never fires → chip would stay hidden forever).
  const [isEditorFocused, setIsEditorFocused] = useState(false);
  const inputColumnRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const check = () => {
      const active = document.activeElement;
      const container = inputColumnRef.current;
      if (!container) return;
      // Only the actual text-entry widgets count — focusing a chip's
      // X button (or the autofill chip itself) shouldn't hide the
      // Autofill chip, and, more importantly, clicking the X on the
      // last chip otherwise leaves us stuck on "focused" because the
      // button unmounts mid-click and its blur event never arrives.
      const isTextEntry =
        (active instanceof HTMLInputElement ||
          active instanceof HTMLTextAreaElement ||
          active instanceof HTMLSelectElement) &&
        container.contains(active);
      setIsEditorFocused(isTextEntry);
    };
    const handle = () => {
      // Defer: focusin/focusout fire before the DOM settles; reading
      // document.activeElement on the next frame gives the real answer.
      window.requestAnimationFrame(check);
    };
    document.addEventListener("focusin", handle);
    document.addEventListener("focusout", handle);
    return () => {
      document.removeEventListener("focusin", handle);
      document.removeEventListener("focusout", handle);
    };
  }, []);
  // Keyed on the attribute id, NOT the context object (#11426, Ankit's
  // 2026-08-29 gap doc item 2): callers build the context inline per render,
  // and the editors below re-fire onChange whenever this callback's identity
  // changes. With the object as a dependency every parent render minted a
  // new callback, the editor's effect wrote the draft store, the tab
  // re-rendered, and React aborted with "Maximum update depth exceeded" —
  // the whole page replaced by the error boundary on any AI-autofill field.
  const autofillAttributeId = autofillContext?.attributeId ?? null;
  const handleChange = useCallback(
    (dirty: boolean, draftValue: ScalarFactValue | ArrayFactValue) => {
      setIsLocallyEmpty(computeIsEmpty(draftValue));
      setIsDirty(dirty);
      // A manual edit invalidates the last autofill outcome for this
      // attribute — otherwise a stale "not_found" from an earlier click
      // sticks around and re-renders red the next time the field goes
      // empty (e.g. the user erases a value that was actually filled
      // successfully before the not_found attempt).
      if (dirty && autofillAttributeId !== null) {
        clearAutofillStatus(autofillAttributeId);
      }
      onChange?.(dirty, draftValue);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onChange, isArray, autofillAttributeId, clearAutofillStatus]
  );

  // The chip yields the leading-adornment slot once the user is putting
  // something in the field. For a scalar input that is the draft going
  // non-empty (`isLocallyEmpty`); focus alone is not it — the Facts grid
  // focuses a row's input the moment it opens (#11880), which hid the chip on
  // every unfilled text and number field (#12035). The entity search box is
  // the one place focus still hides it: typed search text is not a draft
  // value, so it would otherwise sit under the chip.
  const isEntityTyped = !!(
    definition.entity_types && definition.entity_types.length > 0
  );
  const showAutofillChip =
    !!autofillContext && isLocallyEmpty && !(isEditorFocused && isEntityTyped);
  const showSourceChip =
    !isLocallyEmpty && !isDirty && !!sourceQueryId && !!sourceWorkspaceId;
  // Autofill provenance wins the slot when a fact somehow carries both —
  // its dialog shows the extraction evidence in place.
  // The chip's existence tracks provenance; whether it is a link or the
  // attributed tooltip is decided at render (viewable + a destination).
  const showChatSourceChip =
    !isLocallyEmpty && !isDirty && !showSourceChip && !!chatSource;

  if (compact) {
    // Vertical stacked layout for table cell popover
    // Label is already shown in the popover header (ArrayFactPopover / TableCellEditor),
    // so only render the editor here.
    return (
      <div
        className={
          canEditRms && !isAutofillPending
            ? ""
            : "opacity-50 pointer-events-none"
        }
      >
        {isArray ? (
          <ArrayValueEditor
            definition={definition}
            initialValues={fact.array}
            initialDraft={arrayDraft}
            onChange={handleChange}
            currentEntityId={currentEntityId}
            portalDropdown={portalDropdown}
          />
        ) : (
          <ScalarValueEditor
            definition={definition}
            initialValue={fact as ScalarFact}
            initialDraft={scalarDraft}
            onChange={handleChange}
            currentEntityId={currentEntityId}
            portalDropdown={portalDropdown}
          />
        )}
      </div>
    );
  }

  // For composite facts (scalar + entity, like a person's Role), the
  // editor renders a vertical stack with "Value"/"Related Entity"
  // sub-labels — the leading-adornment Autofill chip is rendered inside
  // the scalar sub-input by ScalarValueEditor itself, not by the parent.
  const isCompositeField = !!(definition.scalar_type && isEntityTyped);
  const showOuterAutofillChip = showAutofillChip && !isCompositeField;
  const showOuterSourceChip = showSourceChip && !isCompositeField;
  const showOuterChatSourceChip = showChatSourceChip && !isCompositeField;
  // Input padding scales with whichever chips are rendered in the input
  // column. Tailwind's arbitrary-variant selectors push the inner
  // <input>/<textarea>/<select> text past the absolutely-positioned chip
  // so typed content never overlaps. Entity-typed fields also paint a
  // leading magnifying-glass icon shifted to `left-24` (past the chip)
  // inside EntitySearchInput, so the input needs more left padding there
  // (pl-32) to clear both the chip and the icon. Class strings are kept
  // as literal strings so Tailwind's JIT can see them at build time.
  const inputPaddingClasses = [
    showOuterAutofillChip &&
      (isEntityTyped
        ? "[&_input:not([type='checkbox'])]:pl-32 [&_textarea]:pl-32 [&_select]:pl-32"
        : "[&_input:not([type='checkbox'])]:pl-24 [&_textarea]:pl-24 [&_select]:pl-24"),
    (showOuterSourceChip || showOuterChatSourceChip) &&
      "[&_input:not([type='checkbox'])]:pr-24 [&_textarea]:pr-24 [&_select]:pr-24",
  ]
    .filter(Boolean)
    .join(" ");

  // Original horizontal layout
  return (
    <div
      className={`flex items-center justify-between border-b border-gray-100 last:border-b-0 ${
        layout === "grid" ? "py-2" : "px-6 py-4"
      }`}
    >
      <div className="flex-1 min-w-0">
        <div
          className={`flex items-center ${layout === "grid" ? "gap-4" : "gap-6"}`}
        >
          <div
            className={
              layout === "grid"
                ? "w-[148px] flex-shrink-0"
                : "w-1/3 flex-shrink-0"
            }
          >
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-sm font-medium text-gray-900">
                {factDisplayName(definition)}
              </h3>
              {definition.is_required && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                  Required
                </span>
              )}
              <PendingReviewBadge staging={hasStagedValue} />
              {renderInlineAction?.()}
            </div>
            {showTypeMeta && (
              <InputTypeRenderer
                scalarType={definition.scalar_type}
                scalarUnit={definition.scalar_unit}
                entityTypes={definition.entity_types}
                isArray={isArray}
              />
            )}
          </div>
          <div
            ref={inputColumnRef}
            className={`flex-1 min-w-0 relative ${
              layout === "grid" ? "max-w-[440px]" : ""
            } ${inputPaddingClasses} ${
              canEditRms && !isAutofillPending
                ? ""
                : "opacity-50 pointer-events-none"
            } ${
              conflict
                ? "[&_input]:!border-red-400 [&_input]:!ring-1 [&_input]:!ring-red-400 [&_textarea]:!border-red-400 [&_textarea]:!ring-1 [&_textarea]:!ring-red-400 [&_select]:!border-red-400 [&_select]:!ring-1 [&_select]:!ring-red-400"
                : ""
            }`}
          >
            {showOuterAutofillChip && autofillContext && (
              <div
                data-autofill-chip
                className={`${CHIP_ADORNMENT_CLASSES} left-2`}
              >
                <InlineFieldAutofillControl
                  publicEntityId={autofillContext.publicEntityId}
                  attributeId={autofillContext.attributeId}
                  lastOutcomeAt={autofillContext.lastOutcomeAt}
                />
              </div>
            )}
            {showOuterSourceChip && sourceWorkspaceId && sourceQueryId && (
              <div className={`${CHIP_ADORNMENT_CLASSES} right-2`}>
                <button
                  type="button"
                  onClick={() =>
                    useAnswerStore
                      .getState()
                      // Third arg is the dialog's display heading, not a
                      // lookup key -- it must track the label the field shows.
                      .open(
                        sourceWorkspaceId,
                        sourceQueryId,
                        factDisplayName(definition)
                      )
                  }
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-400"
                  aria-label="View the AI answer that produced this value"
                >
                  <ArrowSquareOutIcon size={12} />
                  <span>Source</span>
                </button>
              </div>
            )}
            {showOuterChatSourceChip && (
              <div className={`${CHIP_ADORNMENT_CLASSES} right-2`}>
                {sourceDocument ? (
                  <Tooltip title={sourceDocument.label} describeChild>
                    <button
                      type="button"
                      onClick={() =>
                        openChatCitation({
                          docId: sourceDocument.docId,
                          pageNum: sourceDocument.pageNum ?? undefined,
                          displayLabel: sourceDocument.label,
                        })
                      }
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-400"
                      aria-label="Open the document this value came from"
                    >
                      <ArrowSquareOutIcon size={12} />
                      <span>Source</span>
                    </button>
                  </Tooltip>
                ) : sourceViewable && sourceHref ? (
                  <a
                    href={sourceHref}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-400"
                    aria-label="Open the conversation that set this value"
                  >
                    <ArrowSquareOutIcon size={12} />
                    <span>Source</span>
                  </a>
                ) : (
                  <Tooltip title={sourceTooltip}>
                    {/* tabIndex makes the chip keyboard-focusable so the
                        tooltip is reachable without a mouse and the label
                        is announced. */}
                    <span
                      tabIndex={0}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium text-gray-500 bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-400"
                      aria-label={sourceTooltip}
                    >
                      <span>Source</span>
                    </span>
                  </Tooltip>
                )}
              </div>
            )}
            {isArray ? (
              <ArrayValueEditor
                definition={definition}
                initialValues={fact.array}
                initialDraft={arrayDraft}
                onChange={handleChange}
                currentEntityId={currentEntityId}
                portalDropdown={portalDropdown}
                hasAutofillChip={showOuterAutofillChip}
                hasSourceChip={showSourceChip || showChatSourceChip}
              />
            ) : (
              <ScalarValueEditor
                definition={definition}
                initialValue={fact as ScalarFact}
                initialDraft={scalarDraft}
                attributeId={autofillContext?.attributeId ?? null}
                onChange={handleChange}
                currentEntityId={currentEntityId}
                portalDropdown={portalDropdown}
                hasAutofillChip={showOuterAutofillChip}
                hasSourceChip={showOuterSourceChip || showOuterChatSourceChip}
                compositeAutofillContext={
                  isCompositeField && showAutofillChip ? autofillContext : null
                }
                compositeSource={
                  isCompositeField &&
                  showSourceChip &&
                  sourceWorkspaceId &&
                  sourceQueryId
                    ? { workspaceId: sourceWorkspaceId, queryId: sourceQueryId }
                    : null
                }
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditableFactRenderer;

interface ScalarEditorProps {
  definition: FactDefinition;
  initialValue: ScalarFact;
  // What the editor opens holding, when that is not the stored value (a
  // draft reopened mid-save or after a rejected save, #11880). Read once,
  // at mount; dirtiness is measured against `initialValue` as always.
  initialDraft?: ScalarFactValue;
  // When set, the editor consults the autofill status store on mount and
  // plays a typewriter reveal if this attribute was just filled by an
  // autofill run. Null for contexts with no autofill wiring.
  attributeId?: number | null;
  onChange?: (isDirty: boolean, draftValue: ScalarFactValue) => void;
  currentEntityId?: string | null;
  portalDropdown?: boolean;
  // True when an Autofill chip is painted at the input's leading edge by
  // the parent; nested EntitySearchInputs shift their magnifying-glass /
  // entity-kind icon past the chip so they don't stack on top of it.
  hasAutofillChip?: boolean;
  // True when a Source chip is painted at the input's trailing edge. The
  // enum StatusDropdown trigger (a <button>, not a native <select>) needs
  // to know so it can reserve right-side padding and keep the selected-
  // value text from running under the chip.
  hasSourceChip?: boolean;
  // For composite facts (scalar + entity), the leading-adornment chip
  // can't be painted by the parent's input column — it would collide
  // with the "Value"/"Related Entity" sub-labels stacked above each
  // sub-input. Instead the composite branch renders the chip as a
  // leading adornment on the scalar sub-input only. Null elsewhere.
  compositeAutofillContext?: {
    publicEntityId: string;
    attributeId: number;
    lastOutcomeAt: string | null;
  } | null;
  // Same idea for the Source chip on composite facts: rendered as a
  // trailing adornment on the scalar sub-input rather than floating
  // outside the editor over the field-name area.
  compositeSource?: { workspaceId: string; queryId: string } | null;
}

const ScalarValueEditor: React.FC<ScalarEditorProps> = ({
  definition,
  initialValue,
  initialDraft,
  attributeId = null,
  onChange,
  currentEntityId = null,
  portalDropdown = false,
  hasAutofillChip = false,
  hasSourceChip = false,
  compositeAutofillContext = null,
  compositeSource = null,
}) => {
  // Non-null when the attribute's designation already settles what kind a new
  // record created from this field is (#11662).
  const createEntityKind = designatedCreateKind(
    definition.designates,
    definition.entity_types
  );
  // Extract the FactValue from the ScalarFact
  const factValue: FactValue = {
    scalar: initialValue.scalar,
    entity: initialValue.entity,
    scalar_end: initialValue.scalar_end ?? null,
    scalar_note: initialValue.scalar_note ?? null,
  };

  const [editValue, setEditValue] = useState<FactValue>(
    initialDraft ?? factValue
  );

  // When a fresh autofill writes a new fact for this attribute, the
  // server's queryId changes. If the user has an unsaved local draft
  // (e.g. they cleared the field then clicked Autofill), the renderer's
  // key may still be pinned to the pre-clear baseline and the editor
  // wouldn't otherwise pick up the new server value. Watching the
  // incoming queryId here forces editValue back in sync with the prop
  // whenever the autofill pipeline produces a new write — every run
  // gets a fresh query_id, so this fires exactly when we want it to.
  //
  // Only a CHANGE of queryId resets the editor. Run on mount as well, this
  // threw away `initialDraft` before the user ever saw it (#11880).
  const incomingQueryId = (initialValue as { queryId?: string | null }).queryId;
  const seenQueryId = useRef(incomingQueryId);
  useEffect(() => {
    if (seenQueryId.current === incomingQueryId) return;
    seenQueryId.current = incomingQueryId;
    setEditValue({
      scalar: initialValue.scalar,
      entity: initialValue.entity,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomingQueryId]);

  // Check if the current value is different from the initial value.
  // Uses a key-order-agnostic stringify so structured values (e.g. address
  // JSON normalized by PostgreSQL JSONB on round-trip) don't stay dirty.
  const isDirty = stableStringify(editValue) !== stableStringify(factValue);

  // Call onChange whenever editValue changes
  useEffect(() => {
    if (onChange) {
      onChange(isDirty, editValue);
    }
  }, [editValue, isDirty, onChange]);

  const handleChange = (value: FactValue) => {
    setEditValue(value);
  };

  const isEntityOnly =
    definition.entity_types &&
    definition.entity_types.length > 0 &&
    !definition.scalar_type;
  const isScalarOnly =
    definition.scalar_type &&
    (!definition.entity_types || definition.entity_types.length === 0);
  const isComposite =
    definition.scalar_type &&
    definition.entity_types &&
    definition.entity_types.length > 0;

  // Typewriter reveal for freshly-autofilled text values. Skipped for
  // entity-typed fields (the value is an entity row, not a string) and
  // non-text scalars (partial numbers/dates/enums don't read as a reveal).
  // Gated on consumeJustFilled() so we only animate on the mount that
  // follows a websocket-delivered 'filled' outcome — not on initial page
  // loads of an entity whose value was autofilled some time ago.
  const isAnimatableScalar =
    isScalarOnly &&
    definition.scalar_type === "string" &&
    !definition.is_hierarchical &&
    !(definition.scalar_values && definition.scalar_values.length > 0);
  // Mount-time one-shot reveal, consumed once via the lazy initializer
  // (`consumeJustFilled` has the side effect of clearing the flag, so it
  // must run exactly once). The text is the scalar as it was at mount, so a
  // later value (the user's own edit) never replays the reveal.
  // `useTypewriter` drives the reveal off the shared product-timer module;
  // feeding it "" ends the animation at once when cancelled (#12187).
  const [fullRevealText] = useState<string | null>(() => {
    if (
      !isAnimatableScalar ||
      attributeId === null ||
      typeof initialValue.scalar !== "string" ||
      initialValue.scalar.length === 0
    ) {
      return null;
    }
    return useAutofillStatusStore.getState().consumeJustFilled(attributeId)
      ? initialValue.scalar
      : null;
  });
  const [typewriterCancelled, setTypewriterCancelled] =
    useState<boolean>(false);
  const revealing: boolean = fullRevealText !== null && !typewriterCancelled;
  const revealedText = useTypewriter(
    revealing && fullRevealText !== null ? fullRevealText : "",
    { charsPerStep: 1, stepMs: 18 }
  );
  const animatedText: string | null =
    revealing &&
    fullRevealText !== null &&
    revealedText.length < fullRevealText.length
      ? revealedText
      : null;
  const cancelTypewriter = useCallback(() => {
    setTypewriterCancelled(true);
  }, []);

  // Prefer the attribute's user-authored description as input placeholder
  // text — the field is self-documenting and we don't have to maintain a
  // separate set of hard-coded hints.
  const descriptionPlaceholder = definition.description || undefined;
  const scalarPlaceholder = descriptionPlaceholder;

  // Entity-only case
  if (isEntityOnly) {
    return (
      <EntitySearchInput
        value={editValue.entity}
        entityTypes={definition.entity_types!}
        hideTypeIndicators={false}
        onSelect={(entity) => handleChange({ scalar: null, entity })}
        variant="dropdown"
        excludeEntityId={currentEntityId}
        allow_create_entity={true}
        createEntityKind={createEntityKind}
        portalDropdown={portalDropdown}
        placeholder={descriptionPlaceholder}
        leadingIconOffset={hasAutofillChip ? "left-24" : "left-3"}
      />
    );
  }

  // Composite case (both scalar and entity)
  if (isComposite) {
    // Per-fact sub-label overrides. The generic "Value"/"Related Entity"
    // pair reads as nondescript next to a fact like Role, where Title /
    // Employer are the names users actually use. Keep this list short
    // and explicit — it's a UX-readability override, not a generic
    // schema feature.
    const compositeLabels: Record<string, { scalar: string; entity: string }> =
      {
        Role: { scalar: "Title", entity: "Employer" },
      };
    const labels = compositeLabels[definition.name] ?? {
      scalar: "Value",
      entity: "Related Entity",
    };
    return (
      <div className="space-y-2">
        {/* Scalar input */}
        <div>
          <label className="text-xs text-gray-500">{labels.scalar}</label>
          <div
            className={`relative ${
              compositeAutofillContext
                ? "[&_input:not([type='checkbox'])]:pl-24 [&_textarea]:pl-24 [&_select]:pl-24"
                : ""
            } ${
              compositeSource
                ? "[&_input:not([type='checkbox'])]:pr-24 [&_textarea]:pr-24 [&_select]:pr-24"
                : ""
            }`}
          >
            {compositeAutofillContext && (
              <div
                data-autofill-chip
                className={`${CHIP_ADORNMENT_CLASSES} left-2`}
              >
                <InlineFieldAutofillControl
                  publicEntityId={compositeAutofillContext.publicEntityId}
                  attributeId={compositeAutofillContext.attributeId}
                  lastOutcomeAt={compositeAutofillContext.lastOutcomeAt}
                />
              </div>
            )}
            {compositeSource && (
              <div className={`${CHIP_ADORNMENT_CLASSES} right-2`}>
                <button
                  type="button"
                  onClick={() =>
                    useAnswerStore
                      .getState()
                      // Display heading, not a lookup key -- see the scalar
                      // Source chip above.
                      .open(
                        compositeSource.workspaceId,
                        compositeSource.queryId,
                        factDisplayName(definition)
                      )
                  }
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-400"
                  aria-label="View the AI answer that produced this value"
                >
                  <ArrowSquareOutIcon size={12} />
                  <span>Source</span>
                </button>
              </div>
            )}
            <ScalarInput
              scalarType={definition.scalar_type}
              scalarUnit={definition.scalar_unit}
              scalarValues={definition.scalar_values}
              scalarEnd={editValue.scalar_end}
              scalarNote={editValue.scalar_note}
              value={editValue.scalar}
              onChange={(scalar) => handleChange({ ...editValue, scalar })}
              isHierarchical={definition.is_hierarchical}
              placeholder={scalarPlaceholder}
            />
          </div>
        </div>
        {/* Entity input — no description placeholder; the description
            already shows under the scalar input above and repeating it
            here reads as a duplicate. */}
        <div>
          <label className="text-xs text-gray-500">{labels.entity}</label>
          <EntitySearchInput
            value={editValue.entity}
            entityTypes={definition.entity_types!}
            hideTypeIndicators={false}
            onSelect={(entity) => handleChange({ ...editValue, entity })}
            variant="dropdown"
            excludeEntityId={currentEntityId}
            allow_create_entity={true}
            createEntityKind={createEntityKind}
            portalDropdown={portalDropdown}
          />
        </div>
      </div>
    );
  }

  // Scalar-only case
  if (isScalarOnly) {
    // All enums route through StatusDropdown — the native <select> (used by
    // DropdownInput inside ScalarInput) renders OS-default gray chrome that
    // clashes with every other editor in this column. StatusDropdown degrades
    // to white trigger + gray border when no enum_colors are provided.
    if (
      definition.scalar_values &&
      definition.scalar_values.length > 0 &&
      !definition.is_hierarchical
    ) {
      return (
        <StatusDropdown
          value={editValue.scalar ? String(editValue.scalar) : ""}
          onChange={(value) => handleChange({ scalar: value, entity: null })}
          options={definition.scalar_values.map(String)}
          toneSource={definition}
          enumLabels={definition.enum_labels}
          hasAutofillChip={hasAutofillChip}
          hasSourceChip={hasSourceChip}
        />
      );
    }

    return (
      <ScalarInput
        scalarType={definition.scalar_type}
        scalarUnit={definition.scalar_unit}
        scalarValues={definition.scalar_values}
        scalarEnd={editValue.scalar_end}
        scalarNote={editValue.scalar_note}
        value={animatedText !== null ? animatedText : editValue.scalar}
        onChange={(scalar) => {
          cancelTypewriter();
          handleChange({ scalar, entity: null });
        }}
        isHierarchical={definition.is_hierarchical}
        placeholder={scalarPlaceholder}
        hasAutofillChip={hasAutofillChip}
        hasSourceChip={hasSourceChip}
      />
    );
  }

  return null;
};

interface ArrayEditorProps {
  definition: FactDefinition;
  initialValues: FactValue[];
  // As `ScalarEditorProps.initialDraft`: what the editor opens holding when
  // a draft is being carried back into it (#11880). Read once, at mount.
  initialDraft?: FactValue[];
  onChange?: (isDirty: boolean, draftValue: ArrayFactValue) => void;
  currentEntityId?: string | null;
  portalDropdown?: boolean;
  // True when an Autofill chip is painted at the input's leading edge by
  // the parent; the nested EntitySearchInput shifts its leading icon past
  // the chip so they don't stack on top of it.
  hasAutofillChip?: boolean;
  // True when a Source chip is painted at the input's trailing edge.
  // Forwarded to StatusDropdown (colored enum trigger button) so it can
  // reserve right-side padding.
  hasSourceChip?: boolean;
}

const ArrayValueEditor: React.FC<ArrayEditorProps> = ({
  definition,
  initialValues,
  initialDraft,
  onChange,
  currentEntityId = null,
  portalDropdown = false,
  hasAutofillChip = false,
  hasSourceChip = false,
}) => {
  const createEntityKind = designatedCreateKind(
    definition.designates,
    definition.entity_types
  );
  const [editValues, setEditValues] = useState<FactValue[]>(
    initialDraft ?? initialValues
  );
  const firstInputRef = useRef<HTMLInputElement | HTMLSelectElement | null>(
    null
  );
  const chipsRef = useRef<HTMLDivElement>(null);
  const dropdownButtonRef = useRef<HTMLButtonElement>(null);

  const handleNavigateToInput = () => {
    if (dropdownButtonRef.current) {
      dropdownButtonRef.current.focus();
    } else if (firstInputRef.current) {
      firstInputRef.current.focus();
    }
  };

  const handleNavigateToChips = () => {
    chipsRef.current?.focus();
  };

  // Note: Auto-focus is disabled here to prevent unwanted focus in non-table contexts
  // Auto-focus is handled by TableCellEditor when in table edit mode

  // Check if the current values are different from the initial values
  const isDirty =
    stableStringify(editValues) !== stableStringify(initialValues);

  // Call onChange whenever editValues changes
  useEffect(() => {
    if (onChange) {
      onChange(isDirty, editValues);
    }
  }, [editValues, isDirty, onChange]);

  // Functional updaters: the input commits its pending text on blur, and a
  // click on a chip's remove button blurs the input in the same task, so an
  // add and a remove can land back to back. Computed from the closed-over
  // array, whichever landed second won and the other was lost (the hazard
  // InteractionCustomFields documents); computed from `prev`, both apply.
  const handleAdd = (newValue: FactValue) => {
    setEditValues((prev) => [...prev, newValue]);
  };

  const handleRemove = (index: number) => {
    setEditValues((prev) => prev.filter((_, i) => i !== index));
  };

  const isEntityOnly =
    definition.entity_types &&
    definition.entity_types.length > 0 &&
    !definition.scalar_type;
  const isScalarOnly =
    definition.scalar_type &&
    (!definition.entity_types || definition.entity_types.length === 0);
  const isComposite =
    definition.scalar_type &&
    definition.entity_types &&
    definition.entity_types.length > 0;

  // Array of entities
  if (isEntityOnly) {
    // Extract entities from editValues for filtering
    const selectedEntities = editValues
      .map((val) => val.entity)
      .filter((e): e is NonNullable<typeof e> => e !== null);

    return (
      <div className="w-full space-y-2">
        <FactValueChips
          ref={chipsRef}
          values={editValues}
          onRemove={handleRemove}
          schema={definition}
          onNavigateToInput={handleNavigateToInput}
          hasSourceChip={hasSourceChip}
        />
        <EntitySearchInput
          value={null}
          entityTypes={definition.entity_types!}
          hideTypeIndicators={false}
          selectedEntities={selectedEntities}
          onSelect={(entity) => {
            if (entity) {
              handleAdd({ scalar: null, entity });
            }
          }}
          variant="dropdown"
          autoFocus={false}
          excludeEntityId={currentEntityId}
          allow_create_entity={true}
          createEntityKind={createEntityKind}
          portalDropdown={portalDropdown}
          leadingIconOffset={hasAutofillChip ? "left-24" : "left-3"}
        />
      </div>
    );
  }

  // Array of scalar values (including enums)
  if (isScalarOnly) {
    // Handle hierarchical arrays specially
    if (definition.is_hierarchical) {
      return (
        <HierarchicalArrayInput
          scalarType={definition.scalar_type}
          scalarUnit={definition.scalar_unit}
          scalarValues={definition.scalar_values}
          values={editValues}
          onAdd={(hierarchy) => handleAdd({ scalar: hierarchy, entity: null })}
          onRemove={handleRemove}
          hasAutofillChip={hasAutofillChip}
          hasSourceChip={hasSourceChip}
        />
      );
    }

    return (
      <div className="w-full space-y-2">
        <FactValueChips
          ref={chipsRef}
          values={editValues}
          onRemove={handleRemove}
          schema={definition}
          onNavigateToInput={handleNavigateToInput}
          hasSourceChip={hasSourceChip}
        />
        <ArrayInput
          scalarType={definition.scalar_type}
          scalarValues={definition.scalar_values}
          existingValues={editValues}
          onAdd={(scalar) => handleAdd({ scalar, entity: null })}
          inputRef={firstInputRef}
          toneSource={definition}
          enumLabels={definition.enum_labels}
          dropdownRef={dropdownButtonRef}
          onNavigateUp={handleNavigateToChips}
          hasAutofillChip={hasAutofillChip}
          hasSourceChip={hasSourceChip}
        />
      </div>
    );
  }

  // Array of composite values (both scalar and entity)
  if (isComposite) {
    // Extract entities from editValues for filtering
    const selectedEntities = editValues
      .map((val) => val.entity)
      .filter((e): e is NonNullable<typeof e> => e !== null);

    return (
      <div className="w-full space-y-2">
        <FactValueChips
          ref={chipsRef}
          values={editValues}
          onRemove={handleRemove}
          schema={definition}
          onNavigateToInput={handleNavigateToInput}
          hasSourceChip={hasSourceChip}
        />
        <CompositeArrayInput
          definition={definition}
          onAdd={handleAdd}
          selectedEntities={selectedEntities}
          inputRef={firstInputRef}
          currentEntityId={currentEntityId}
          portalDropdown={portalDropdown}
        />
      </div>
    );
  }

  return null;
};

interface ArrayInputProps {
  scalarType: ScalarKind | null;
  scalarValues?: string[] | null;
  existingValues?: FactValue[];
  onAdd: (scalar: string | number | boolean | null) => void;
  inputRef?: React.RefObject<HTMLInputElement | HTMLSelectElement | null>;
  toneSource?: ToneSource | null;
  enumLabels?: Record<string, string> | null;
  dropdownRef?: React.RefObject<HTMLButtonElement | null>;
  onNavigateUp?: () => void;
  // Forwarded to a nested colored-enum StatusDropdown (the button
  // variant doesn't inherit the parent's `[&_select]:pl-24` selectors).
  hasAutofillChip?: boolean;
  hasSourceChip?: boolean;
}

const ArrayInput: React.FC<ArrayInputProps> = ({
  scalarType,
  scalarValues,
  existingValues = [],
  onAdd,
  inputRef,
  toneSource,
  enumLabels,
  dropdownRef,
  onNavigateUp,
  hasAutofillChip = false,
  hasSourceChip = false,
}) => {
  const [inputValue, setInputValue] = useState<string>("");

  // Multi-select with enum values - use dropdown with filtered options
  if (scalarValues && scalarValues.length > 0) {
    // Filter out already selected values
    const availableOptions = scalarValues.filter(
      (option) => !existingValues.some((v) => v.scalar === option)
    );

    return (
      <StatusDropdown
        ref={dropdownRef}
        value=""
        onChange={(value) => {
          if (value) {
            onAdd(value);
          }
        }}
        options={availableOptions}
        toneSource={toneSource}
        enumLabels={enumLabels}
        placeholder={
          availableOptions.length > 0 ? "Add an option" : "All options selected"
        }
        onNavigateUp={onNavigateUp}
        hasAutofillChip={hasAutofillChip}
        hasSourceChip={hasSourceChip}
      />
    );
  }

  // For booleans, use dropdown with Yes/No
  if (scalarType === "boolean") {
    return (
      <StatusDropdown
        ref={dropdownRef}
        value=""
        onChange={(value) => {
          if (value) {
            onAdd(value === "Yes");
          }
        }}
        options={["Yes", "No"]}
        placeholder="Add an option"
        onNavigateUp={onNavigateUp}
        hasAutofillChip={hasAutofillChip}
        hasSourceChip={hasSourceChip}
      />
    );
  }

  // For dates, use DateInput with immediate add
  if (scalarType === "date") {
    return (
      <DateInput
        value={inputValue}
        onChange={(value) => {
          if (value) {
            onAdd(value);
            setInputValue("");
          }
        }}
        inputRef={inputRef as React.RefObject<HTMLInputElement>}
      />
    );
  }

  // Text and numbers commit on Enter AND on leaving the input (#11426,
  // Ankit's 2026-08-29 gap doc item 4): a value typed and then abandoned for
  // the Create / Save button used to be discarded silently, which read as
  // the field "blanking out". Whitespace is not a value.
  const commitPending = () => {
    if (inputValue.trim() === "") return;
    const value = scalarType === "number" ? Number(inputValue) : inputValue;
    onAdd(value);
    setInputValue("");
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && inputValue) {
      e.preventDefault();
      commitPending();
    }
  };

  return (
    <input
      ref={inputRef as React.RefObject<HTMLInputElement>}
      type={scalarType === "number" ? "number" : "text"}
      value={inputValue}
      onChange={(e) => setInputValue(e.target.value)}
      onKeyDown={handleKeyDown}
      onBlur={commitPending}
      placeholder="Add a value"
      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-[var(--fp-ink-3)] focus:border-[var(--fp-ink-3)]"
    />
  );
};

interface CompositeArrayInputProps {
  definition: FactDefinition;
  onAdd: (value: FactValue) => void;
  selectedEntities?: ShallowEntity[];
  inputRef?: React.RefObject<HTMLInputElement | HTMLSelectElement | null>;
  currentEntityId?: string | null;
  portalDropdown?: boolean;
}

const CompositeArrayInput: React.FC<CompositeArrayInputProps> = ({
  definition,
  onAdd,
  selectedEntities = [],
  inputRef,
  currentEntityId = null,
  portalDropdown = false,
}) => {
  const createEntityKind = designatedCreateKind(
    definition.designates,
    definition.entity_types
  );
  const [scalarValue, setScalarValue] = useState<any>(null);
  const [entityValue, setEntityValue] = useState<ShallowEntity | null>(null);

  const handleAdd = () => {
    // Only add if we have at least one value (scalar or entity)
    if (scalarValue !== null || entityValue !== null) {
      onAdd({
        scalar: scalarValue,
        entity: entityValue,
      });
      // Reset the form
      setScalarValue(null);
      setEntityValue(null);
    }
  };

  return (
    <div className="flex flex-col sm:flex-row gap-2 items-end">
      {/* Scalar input */}
      <div className="flex-1 w-full">
        <label className="text-xs text-gray-500">Value</label>
        <ScalarInput
          scalarType={definition.scalar_type}
          scalarUnit={definition.scalar_unit}
          scalarValues={definition.scalar_values}
          value={scalarValue}
          onChange={setScalarValue}
          isHierarchical={definition.is_hierarchical}
          inputRef={inputRef}
        />
      </div>

      {/* Entity input */}
      <div className="flex-1 w-full">
        <label className="text-xs text-gray-500">Related Entity</label>
        <EntitySearchInput
          value={entityValue}
          entityTypes={definition.entity_types!}
          hideTypeIndicators={false}
          selectedEntities={selectedEntities}
          onSelect={setEntityValue}
          variant="dropdown"
          excludeEntityId={currentEntityId}
          allow_create_entity={true}
          createEntityKind={createEntityKind}
          portalDropdown={portalDropdown}
        />
      </div>

      {/* Add button - commits the (value, entity) pair to the list.
          Wrapped to mirror the label + input vertical rhythm of the two
          columns so the button's input-row lines up with theirs, instead
          of floating at row bottom with no label spacer above it. */}
      <div className="flex flex-col flex-shrink-0">
        <span aria-hidden="true" className="text-xs invisible">
          &nbsp;
        </span>
        <button
          onClick={handleAdd}
          disabled={scalarValue === null && entityValue === null}
          className="h-[38px] px-3 flex items-center justify-center bg-gray-800 text-white rounded hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
          title="Add to list"
        >
          <PlusIcon size={16} />
        </button>
      </div>
    </div>
  );
};
